import {
  useIsMobile,
  useModalContext,
  WSButton,
  WSElement,
  WSElementProps,
  WSFlexBox,
  WSIcon,
  WSImage,
  WSLoader,
  WSModal,
  WSOverlay,
  WSScreen,
  WSText
} from "@wingspanhq/fe-component-library";
import * as React from "react";
import { Manager, Popper, Reference } from "react-popper";
import { Calendly } from "../../../components/Calendly/Calendly";
import { useUserId } from "../../../query/hooks/helpers";
import { useAdvocate, useMemberProfile } from "../../../query/users/queries";
import { WSQueries } from "../../../query/WSQuery";
import styles from "./AskYourAdvocateWidget.module.scss";

interface AdvocateProfileProps {
  profileUrl: string;
  name: string;
}

export const AdvocateProfile: React.FC<AdvocateProfileProps> = ({
  profileUrl,
  name
}) => {
  return (
    <WSFlexBox className={styles.profile} alignItems="center">
      <WSElement className={styles.imgContainer} mr="S">
        <WSImage src={profileUrl} placeholder="" className={styles.img} />
      </WSElement>
      <WSFlexBox direction="column">
        <WSText.LabelBold mb="XS">{name}</WSText.LabelBold>
        <WSText.LabelSmall>Your member advocate</WSText.LabelSmall>
      </WSFlexBox>
    </WSFlexBox>
  );
};

interface AskYourAdvocateWidgetProps {}

export const AskYourAdvocateWidget: React.FC<AskYourAdvocateWidgetProps> = () => {
  const isMobile = useIsMobile();
  const userId = useUserId();
  const qMember = useMemberProfile(userId);
  const qAdvocate = useAdvocate(qMember.data?.advocateId as string);
  return (
    <WSQueries
      queries={{ qMember, qAdvocate }}
      renderLoader={() => (
        <WSFlexBox.Center className={styles.loader}>
          <WSLoader.Spinner />
        </WSFlexBox.Center>
      )}
    >
      {({ qAdvocate: { data: advocate } }) => (
        <WSFlexBox direction="column" className={styles.advocateWidget}>
          <WSText.Heading3 mb="S">Need assistance?</WSText.Heading3>
          <AdvocateProfile
            profileUrl={advocate?.photoUrl as string}
            name={advocate?.firstName as string}
          />
          <Calendly>
            {({ onOpen }) => (
              <WSButton onClick={onOpen} mt="M" fullWidth={isMobile}>
                {`Schedule a call with ${advocate.firstName}`}
              </WSButton>
            )}
          </Calendly>
        </WSFlexBox>
      )}
    </WSQueries>
  );
};

export const ASK_YOUR_ADVOCATE_MODAL = "ASK_YOUR_ADVOCATE_MODAL";

export const AskYourAdvocateModal: React.FC = () => (
  <WSModal name={ASK_YOUR_ADVOCATE_MODAL} size="S" key="modal">
    <AskYourAdvocateWidget />
  </WSModal>
);

type ButtonProps = WSElementProps;

export const AskYourAdvocateButton: React.FC<ButtonProps> = ({
  ...elementProps
}) => {
  const { openModal } = useModalContext();
  const [popUpIsOpen, setPopUpIsOpen] = React.useState(false);

  return (
    <WSElement {...elementProps}>
      <WSScreen.TabletAndDesktop>
        {popUpIsOpen && (
          <WSOverlay
            onClick={() => {
              setPopUpIsOpen(false);
            }}
          />
        )}
        <Manager>
          <Reference>
            {({ ref }) => (
              <WSElement
                ref={ref}
                onClick={() => {
                  setPopUpIsOpen(true);
                }}
              >
                <WSFlexBox.Center className={styles.button}>
                  <WSIcon name="chat" mr="S" color="blue" />
                  <WSText.LabelBold color="blue">
                    Ask your advocate
                  </WSText.LabelBold>
                </WSFlexBox.Center>
              </WSElement>
            )}
          </Reference>
          {popUpIsOpen && (
            <Popper placement="auto" strategy="absolute">
              {({ ref, style, placement }) => (
                <WSElement
                  ref={ref}
                  className={styles.popupContainer}
                  style={style}
                  data-placement={placement}
                >
                  <WSElement className={styles.popup}>
                    <WSElement
                      className={styles.closeIconContainer}
                      onClick={() => setPopUpIsOpen(false)}
                    >
                      <WSIcon name="close" color="grey600" size="XS" />
                    </WSElement>
                    <AskYourAdvocateWidget />
                  </WSElement>
                </WSElement>
              )}
            </Popper>
          )}
        </Manager>
      </WSScreen.TabletAndDesktop>
      <WSScreen.Mobile>
        <AskYourAdvocateModal />
        <WSFlexBox.Center
          className={styles.button}
          onClick={() => openModal(ASK_YOUR_ADVOCATE_MODAL)}
        >
          <WSIcon name="chat" mr="S" color="blue" />
          <WSText.LabelBold color="blue">Chat</WSText.LabelBold>
        </WSFlexBox.Center>
      </WSScreen.Mobile>
    </WSElement>
  );
};

export const AskYourAdvocateButtonFixed: React.FC<ButtonProps> = ({
  ...props
}) => (
  <>
    <WSScreen.TabletAndDesktop>
      <WSElement className={styles.buttonFixed}>
        <AskYourAdvocateButton {...props} />
      </WSElement>
    </WSScreen.TabletAndDesktop>
    <WSScreen.Mobile>
      <AskYourAdvocateButton {...props} />
    </WSScreen.Mobile>
  </>
);
